import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { PatientOnboardingStatus } from 'services/selfSchedulingServices';
import { AvailabilitySlot, ConsultationType, PatientSelfSchedulingContext, Provider } from './types';

dayjs.extend(utc);

// dto.start/end are office-local wall-clock time with an explicit offset (see
// toScheduledTime below), e.g. "2026-09-10T10:40:00-05:00" for a 10:40am Chicago
// appointment. A patient needs to see that literal office time regardless of their own
// browser's timezone (an in-person visit happens at 10:40am Chicago time no matter where the
// patient is when browsing) - but the offset can't just be left in place either: FullCalendar
// (see AvailabilityCalendar) and plain dayjs(...) both *parse* the offset correctly and then
// convert the resulting instant for display (to true UTC, or to the browser's zone,
// respectively) rather than showing the digits as written. Stripping the offset removes the
// only thing that conversion needs, so both then simply show the literal wall-clock digits.
export const stripOfficeOffset = (iso: string): string => iso.replace(/[+-]\d{2}:?\d{2}$/, '');

const asOfficeLocal = (iso: string) => dayjs.utc(stripOfficeOffset(iso));

export const mapSlot = (dto: any): AvailabilitySlot => {
  const start = asOfficeLocal(dto.start);
  const end = asOfficeLocal(dto.end);
  // Matches the single-provider calendar (AvailabilityCalendar/FullCalendar), which shows a
  // start-end range for a timed event by default - the provider-card preview previously only
  // showed the start time, which read as inconsistent/wrong next to the calendar's range.
  const sameMeridiem = start.format('A') === end.format('A');
  return {
    id: dto.id,
    day: start.format('ddd, MMM D'),
    time: `${start.format(sameMeridiem ? 'h:mm' : 'h:mm A')}-${end.format('h:mm A')}`,
    start: dto.start,
    end: dto.end,
    officeId: dto.officeId,
    officeName: dto.officeName,
    officeCity: dto.officeCity,
    profileId: dto.profileId,
    examRoom: dto.examRoom,
    duration: dto.duration,
  };
};

export const mapProvider = (dto: any): Provider => ({
  id: dto.id,
  name: dto.name,
  credential: dto.credential,
  description: dto.description,
  consultationTypes: (dto.consultationTypes ?? []) as ConsultationType[],
  locations: dto.locations ?? [],
  availability: (dto.availability ?? []).map(mapSlot),
  hasMoreAvailability: Boolean(dto.hasMoreAvailability),
});

// Defaults (no preselected provider, insurance-pay, booking allowed) if the context call fails -
// matches the backend's own defaulting (getPatientContext never throws, a lookup failure just
// degrades to these same defaults) so a transient failure here never blocks the page.
export const mapPatientContext = (dto: any): PatientSelfSchedulingContext => ({
  providerId: dto?.providerId ?? undefined,
  payMode: dto?.payMode ?? 'InsurancePay',
  canBookAppointment: dto?.canBookAppointment ?? true,
  primaryProviderUnavailable: dto?.primaryProviderUnavailable ?? false,
});

// Unlike mapPatientContext above, defaults to "not complete" on a failed/missing fetch - the
// conservative direction for the NEW-patient onboarding blackout this feeds into
// (ehr-sync-service's own PatientOnboardingStatus defaults the same way for an omitted flag), so
// a transient failure here can never accidentally skip the blackout for a patient who hasn't
// actually finished onboarding.
export const mapOnboardingStatus = (dto: any): PatientOnboardingStatus => ({
  profileComplete: dto?.profileComplete ?? false,
  mfshComplete: dto?.mfshComplete ?? false,
  mcatComplete: dto?.mcatComplete ?? false,
  intakePaperworkComplete: dto?.intakePaperworkComplete ?? false,
  migrationStatus: dto?.migrationStatus ?? undefined,
});

export const formatSlotLocation = (slot: Pick<AvailabilitySlot, 'officeName' | 'officeCity'>): string =>
  [slot.officeName, slot.officeCity].filter(Boolean).join(' – ');

/**
 * Provider card heading - "Name, Credentials", or just the name when no credentials are set in the
 * portal (MM-1159).
 */
export const formatProviderName = (provider: Pick<Provider, 'name' | 'credential'>): string =>
  [provider.name, provider.credential?.trim()].filter(Boolean).join(', ');

// The backend returns office-local (America/Chicago) wall-clock time with an explicit
// offset (e.g. '2026-09-10T14:15:00-05:00'). DrChrono's scheduled_time expects that same
// local time with no offset, so this drops it rather than reinterpreting/shifting it.
export const toScheduledTime = (isoWithOffset: string): string => isoWithOffset.slice(0, 19);
